import { Card, Center, Stack, Text, ThemeIcon } from '@mantine/core';
import type { ReactNode } from 'react';

const SIZE_CONFIG = {
  sm: { icon: 48, iconInner: 24, titleSize: 'sm', gap: 'sm' },
  md: { icon: 64, iconInner: 32, titleSize: 'md', gap: 'md' },
  lg: { icon: 80, iconInner: 40, titleSize: 'lg', gap: 'md' },
} as const;

export interface EmptyStateProps {
  /** Icon element rendered inside a ThemeIcon */
  icon: ReactNode;
  /** Title text */
  title: string;
  /** Optional description text */
  description?: string;
  /** Optional action element (button or button group) */
  action?: ReactNode;
  /** Size variant - affects icon and text sizing. Default: 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Wrap in a Card with border. Default: false */
  withCard?: boolean;
  /** Max width for description text. Default: 400 */
  descriptionMaxWidth?: number;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  size = 'md',
  withCard = false,
  descriptionMaxWidth = 400,
}: EmptyStateProps) {
  const config = SIZE_CONFIG[size];

  const content = (
    <Center py="xl">
      <Stack align="center" gap={config.gap}>
        <ThemeIcon size={config.icon} radius="xl" variant="light" color="gray">
          {icon}
        </ThemeIcon>
        <Stack align="center" gap="xs">
          <Text fw={600} size={config.titleSize}>
            {title}
          </Text>
          {description && (
            <Text c="dimmed" ta="center" size="sm" maw={descriptionMaxWidth}>
              {description}
            </Text>
          )}
        </Stack>
        {action}
      </Stack>
    </Center>
  );

  if (withCard) {
    return (
      <Card withBorder p="xl">
        {content}
      </Card>
    );
  }

  return content;
}

export default EmptyState;
